import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSprings, animated } from 'react-spring/web.cjs';
import { useDrag } from 'react-use-gesture';

import { Link } from 'components/Common';
import fsc from 'fsc';
import i18n from 'i18n';
import theme from 'themes';

const Slider = styled.div`
  position: relative;
  height: ${({ isClock }) => (isClock ? 166 : 128)}px;
`;

const ItemContainer = styled(animated.div)`
  position: absolute;
  will-change: transform;
`;

const CollectionRow = ({
  itemWidth,
  apps,
  collectionId,
  ItemClass,
  isClock
}) => {
  const edgeDistance = theme.edgeDistanceRaw - (isClock ? 8 : 0);
  const showCount = Math.floor(window.innerWidth / itemWidth);
  const [active, setActive] = useState(0);
  const [props, set] = useSprings(apps.length, (i) => ({
    x: edgeDistance + itemWidth * i
  }));

  const bind = useDrag(
    ({ down, movement: [mx], direction: [xDir], distance }) => {
      let newActive = active;
      if (!down && distance >= itemWidth / 2) {
        const distanceCount = Math.round(distance / itemWidth);
        setActive((active) => {
          newActive = Math.max(
            0,
            Math.min(
              active + (xDir > 0 ? -distanceCount : distanceCount),
              apps.length - showCount
            )
          );
          return newActive;
        });
      }

      set((i) => ({
        x: edgeDistance + itemWidth * (i - newActive) + (down ? mx : 0),
        immediate: down
      }));
    }
  );

  return (
    <Slider {...bind()} isClock={isClock}>
      {apps.map((app, i) => (
        <ItemContainer
          key={`app-${app.id}-${collectionId}`}
          style={{
            transform: props[i].x.interpolate(
              (x) => `translate3d(${x}px, 0, 0)`
            )
          }}
        >
          <ItemClass app={app} />
        </ItemContainer>
      ))}
    </Slider>
  );
};

CollectionRow.propTypes = {
  apps: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.shape({
        uri: PropTypes.string.isRequired
      }),
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  ItemClass: PropTypes.func.isRequired
};

const CollectionContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  background: white;
  padding-top: 20px;
`;

const CollectionTitle = styled.div`
  font-size: 18px;
  margin: 0 15px 15px;
`;

const ViewAllLink = styled.div`
  color: ${({ theme }) => theme.fitbitGreen};
  float: right;
  font-size: 14px;
  margin-right: 15px;
`;

const Collection = ({
  itemWidth,
  apps,
  hasNextPage,
  collectionId,
  fscViewName,
  name,
  isClock,
  ItemClass,
  collectionType
}) => (
  <CollectionContainer>
    <ViewAllLink>
      <Link
        to={`/${isClock ? 'clocks' : 'apps'}/${collectionType}/${collectionId}`}
        pageDirection="FORWARD"
        data-test-type="collection-view-all-link"
        data-test-id={collectionId}
        onClick={() =>
          fsc
            .view(fscViewName)
            .Tapped('View All')
            .withPayload({ collectionId })
            .send()
        }
      >
        {i18n('component.Category.viewAllText')()}
      </Link>
    </ViewAllLink>
    <CollectionTitle>{name}</CollectionTitle>
    <CollectionRow
      itemWidth={itemWidth}
      apps={apps}
      hasNextPage={hasNextPage}
      collectionId={collectionId}
      isClock={isClock}
      ItemClass={ItemClass}
    />
  </CollectionContainer>
);

Collection.propTypes = {
  apps: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.shape({
        uri: PropTypes.string.isRequired
      }),
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  fscViewName: PropTypes.string.isRequired,
  isClock: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  collectionId: PropTypes.string.isRequired,
  ItemClass: PropTypes.func.isRequired,
  collectionType: PropTypes.string.isRequired
};

export default Collection;
